import React from "react"
import cx from "classnames"
import Img from "gatsby-image"
import { IconChevron, IconPlay } from "../images"
import { useIntl, useCarousel } from "../hooks"
import { useStaticQuery, graphql } from "gatsby"
import colors from "../styles/theme/colors"
import { IconTwitter, IconInstagram } from "../images/Logos/Brands"

type SocialPost = {
  id: number
  name: string
  handle: string
  avatar: string
  desc: string
  media: string
  isVideo: boolean
  socialLink: string
  socialDate: string
  socialIcon: React.ReactNode
}

type Frame = {
  id: number
  image: string
  logo: string
  brandColor: string
  frameNumber: number
  prevFrame: number
  nextFrame: number
  subheading: string
  socialPost: SocialPost
  heading: string
  desc?: string
  quote?: string
  authorAvatar?: string
  authorName?: string
  authorRole?: string
  isStatStyle: boolean
  isStatStack: boolean
  isLastStatFull?: boolean
  statOne?: string
  statOneType?: string
  statTwo?: string
  statTwoType?: string
  statThree?: string
  statThreeType?: string
  socialieStackDesc?: string
  socialieStackLogoSrc?: string
}

interface Props {
  bgColor?: string
}

export default function StoriesCarousel(props: Props) {
  const { bgColor = colors.lightGrey } = props
  const { t } = useIntl()
  const data = useStaticQuery(graphql`
    fragment CustomerStoriesFragment on File {
      id
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    query {
      missUniverse: file(relativePath: { eq: "Logos/miss-universe.png" }) {
        ...CustomerStoriesFragment
      }
      missUniverseBg: file(relativePath: { eq: "CustomerStories/MissUniverse/miss-universe-story.jpg" }) {
        id
        publicURL
      }
      missUniverseMedia: file(relativePath: { eq: "CustomerStories/MissUniverse/miss-universe-thumb.png" }) {
        ...CustomerStoriesFragment
      }
      missUniverseAvatar: file(relativePath: { eq: "CustomerStories/MissUniverse/miss-usa.png" }) {
        ...CustomerStoriesFragment
      }
      ufc: file(relativePath: { eq: "Logos/ufc.png" }) {
        ...CustomerStoriesFragment
      }
      ufcBg: file(relativePath: { eq: "CustomerStories/UFC/ufc-story-new.jpg" }) {
        id
        publicURL
      }
      ufcMedia: file(relativePath: { eq: "CustomerStories/UFC/ufc-media.png" }) {
        ...CustomerStoriesFragment
      }
      ufcAvatar: file(relativePath: { eq: "CustomerStories/UFC/austin-hubbard.png" }) {
        ...CustomerStoriesFragment
      }
      ufcAuthorAvatar: file(relativePath: { eq: "CustomerStories/UFC/david-shaw.png" }) {
        ...CustomerStoriesFragment
      }
      atp: file(relativePath: { eq: "Logos/atp.png" }) {
        ...CustomerStoriesFragment
      }
      atpBg: file(relativePath: { eq: "CustomerStories/ATP/atp-story.jpg" }) {
        id
        publicURL
      }
      atpAvatar: file(relativePath: { eq: "CustomerStories/ATP/novak-djokovic.jpg" }) {
        ...CustomerStoriesFragment
      }
      atpMedia: file(relativePath: { eq: "CustomerStories/ATP/atp-post.jpg" }) {
        ...CustomerStoriesFragment
      }
      atpAuthorAvatar: file(relativePath: { eq: "CustomerStories/ATP/ross-hutchins.jpg" }) {
        ...CustomerStoriesFragment
      }
    }
  `)
  const frames: Frame[] = [
    {
      id: 1,
      image: data.missUniverseBg.publicURL,
      logo: data.missUniverse.childImageSharp.fluid,
      brandColor: colors.missUniverse,
      frameNumber: 0,
      prevFrame: 2,
      nextFrame: 1,
      subheading: t("CustomerStories__Subheading"),
      heading: t("CustomerStories__FrameOne--Heading"),
      desc: t("CustomerStories__FrameOne--Desc"),
      socialPost: {
        id: 1,
        name: t("CustomerStories__FrameOne--SocialPost-Name"),
        handle: t("CustomerStories__FrameOne--SocialPost-Handle"),
        isVideo: true,
        avatar: data.missUniverseAvatar.childImageSharp.fluid,
        socialLink: "https://twitter.com/MissUSA/status/1122694445588074496",
        desc: t("CustomerStories__FrameOne--SocialPost-Desc"),
        media: data.missUniverseMedia.childImageSharp.fluid,
        socialDate: t("CustomerStories__FrameOne--SocialPost-SocialDate"),
        socialIcon: <IconTwitter className="mr-1 w-2 h-2 text-twitter" />,
      },
      isStatStyle: true,
      isStatStack: false,
      isLastStatFull: true,
      statOne: t("CustomerStories__FrameOne--StatOne"),
      statOneType: t("CustomerStories__FrameOne--StatOneType"),
      statTwo: t("CustomerStories__FrameOne--StatTwo"),
      statTwoType: t("CustomerStories__FrameOne--StatTwoType"),
      statThree: t("CustomerStories__FrameOne--StatThree"),
      statThreeType: t("CustomerStories__FrameOne--StatThreeType"),
    },
    {
      id: 2,
      image: data.ufcBg.publicURL,
      logo: data.ufc.childImageSharp.fluid,
      heading: t("CustomerStories__FrameTwo--Heading"),
      subheading: t("CustomerStories__Subheading"),
      socialPost: {
        id: 2,
        name: t("CustomerStories__FrameTwo--SocialPost-Name"),
        handle: t("CustomerStories__FrameTwo--SocialPost-Handle"),
        isVideo: false,
        avatar: data.ufcAvatar.childImageSharp.fluid,
        socialLink: "https://www.instagram.com/p/CBrP0pVp7Yf/",
        desc: t("CustomerStories__FrameTwo--SocialPost-Desc"),
        media: data.ufcMedia.childImageSharp.fluid,
        socialDate: t("CustomerStories__FrameTwo--SocialPost-SocialDate"),
        socialIcon: <IconInstagram className="mr-1 w-2 h-2 text-instagram" />,
      },
      isStatStyle: false,
      isStatStack: false,
      brandColor: "#1b1823",
      frameNumber: 1,
      prevFrame: 0,
      nextFrame: 2,
      quote: t("CustomerStories__FrameTwo--Quote"),
      authorAvatar: data.ufcAuthorAvatar.childImageSharp.fluid,
      authorName: t("CustomerStories__FrameTwo--Author-Name"),
      authorRole: t("CustomerStories__FrameTwo--Author-Role"),
    },
    {
      id: 3,
      image: data.atpBg.publicURL,
      logo: data.atp.childImageSharp.fluid,
      heading: t("CustomerStories__FrameFour--Heading"),
      subheading: t("CustomerStories__Subheading"),
      frameNumber: 2,
      prevFrame: 1,
      nextFrame: 0,
      socialPost: {
        id: 3,
        name: t("CustomerStories__FrameFour--SocialPost-Name"),
        handle: t("CustomerStories__FrameFour--SocialPost-Handle"),
        isVideo: false,
        avatar: data.atpAvatar.childImageSharp.fluid,
        socialLink: "https://www.instagram.com/p/Bz6M5MOn4Ht/",
        desc: t("CustomerStories__FrameFour--SocialPost-Desc"),
        media: data.atpMedia.childImageSharp.fluid,
        socialDate: t("CustomerStories__FrameFour--SocialPost-SocialDate"),
        socialIcon: <IconInstagram className="mr-1 w-2 h-2 text-instagram" />,
      },
      isStatStyle: false,
      isStatStack: false,
      brandColor: "#393739",
      quote: t("CustomerStories__FrameFour--Quote"),
      authorAvatar: data.atpAuthorAvatar.childImageSharp.fluid,
      authorName: t("CustomerStories__FrameFour--Author-Name"),
      authorRole: t("CustomerStories__FrameFour--Author-Role"),
    },
  ]

  const [currentFrame, setCurrentFrame, handlers, style] = useCarousel(frames.length)
  return (
    <div className="w-screen">
      <div className="relative overflow-hidden">
        <div
          className="absolute w-screen h-30vh z-50"
          style={{
            background: `linear-gradient(-5deg, ${colors.transparent} 49%, ${colors.transparent} 1%, ${bgColor} 49.5%)`,
          }}
        />
        <div {...handlers}>
          <div
            className="flex pb-3 phablet:pb-3.75 tablet:pb-4.5 laptop:pb-6 se:h-125 phablet:h-125"
            style={{ width: `${frames.length * 100}%` }}
          >
            {frames.map((frame) => {
              return (
                <CarouselFrame
                  key={frame.id}
                  frame={frame}
                  frames={frames}
                  style={style}
                  currentFrame={currentFrame}
                  setCurrentFrame={setCurrentFrame}
                />
              )
            })}
          </div>
        </div>
        <div className="absolute bottom-0 w-full flex justify-center space-x-3">
          {frames.map((frame) => {
            return (
              <div
                key={frame.id}
                onClick={() => setCurrentFrame(frame.frameNumber)}
                className="w-6 h-6 phablet:w-7.5 phablet:h-7.5 tablet:w-9 tablet:h-9 laptop:w-12 laptop:h-12 rounded-full bg-white"
              >
                <Img
                  className={cx(
                    "w-full h-full rounded-full bg-white cursor-pointer hover:opacity-100 transition-opacity duration-200 ease-in-out",
                    {
                      "opacity-50": frame.frameNumber !== currentFrame,
                    },
                  )}
                  alt="socialie logo"
                  fluid={frame.logo}
                />
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

interface CarouselFrameProps {
  frame: Frame
  frames: Frame[]
  style: React.CSSProperties
  currentFrame: number
  setCurrentFrame: (n: number) => void
}

function CarouselFrame(props: CarouselFrameProps) {
  const { frame, frames, style, currentFrame, setCurrentFrame } = props

  const transitionTime = 900
  const smooth = `transform ${transitionTime}ms cubic-bezier(0.76, 0, 0.24, 1)`
  const gradient =
    "linear-gradient(to bottom, rgba(52, 61, 70, 0.5), rgba(52, 61, 70, 0.5)), linear-gradient(237deg, rgba(252,35,97,0.5) 99%, rgba(92,199,234,0.5))"
  return (
    <div
      key={frame.id}
      className="min-h-full w-full flex items-start overlay-util"
      style={{
        ...style,
        transition: smooth,
        background: `${gradient}, url(${frame.image}) no-repeat center`,
        backgroundSize: "cover",
        backgroundBlendMode: "normal, soft-light",
      }}
    >
      <div className="mx-auto laptop:w-containerMax max-w-containerMax pt-30vh mt-3 mb-9 flex items-center">
        <div className="flex flex-col-mt-6 mb-5">
          <div className="container">
            <div className="mx-auto w-full phablet:w-8/12 tablet:w-8/12 laptop:w-62">
              <div className="mb-1.5 w-full hidden tablet:flex laptop:flex ">
                <div className="w-1/2">
                  <div
                    className="flex items-center justify-start cursor-pointer"
                    onClick={() => setCurrentFrame(frame.prevFrame)}
                  >
                    <IconChevron className="mr-1 h-1.75 w-1.75 text-white transform rotate-180 cursor-pointer" />
                    <p className="text-p leading-h6 tracking-10 text-white font-black">
                      {frames[frame.prevFrame].heading}
                    </p>
                  </div>
                </div>
                <div className="w-1/2">
                  {frame.frameNumber !== frame.nextFrame && (
                    <div
                      className="flex items-center justify-end cursor-pointer"
                      onClick={() => setCurrentFrame(frame.nextFrame)}
                    >
                      <p className="text-p leading-h6 tracking-10 text-white font-black">
                        {frames[frame.nextFrame].heading}
                      </p>
                      <IconChevron className="ml-1 h-1.75 w-1.75 text-white transform" />
                    </div>
                  )}
                </div>
              </div>
              <div
                className={cx(
                  "h-full p-4 se:max-w-40 relative flex items-start tablet:items-center laptop:items-center rounded-32 bg-white",
                  {
                    "transform transition-all ease-in-out duration-1000 se:-translate-x-3 phablet:-translate-x-5/12 tablet:-translate-x-5/12 laptop:-translate-x-6/12":
                      frame.frameNumber === frames[currentFrame].nextFrame,
                    "transform transition-all ease-in-out duration-700 translate-x-0 opacity-100":
                      frame.frameNumber === frames[currentFrame].frameNumber,
                  },
                )}
              >
                <div className="hidden laptop:block absolute h-full left-10/12 top-4">
                  <a
                    href={frame.socialPost.socialLink}
                    className="cursor-pointer"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="max-w-43.5 px-2 pt-3 pb-2 z-20 left-0 bg-white rounded-4 shadow-lg">
                      <div className="mb-1.5 w-full flex items-center justify-start">
                        <Img
                          className="mr-1.5 w-4 h-4 rounded-full"
                          fluid={frame.socialPost.avatar}
                          alt={`${frame.socialPost.name} avatar`}
                        />
                        <div className="mr-0.5">
                          <p className="text-small leading-small tracking-10 text-bodyText font-black">
                            {frame.socialPost.name}
                          </p>
                        </div>
                        <p className="text-small leading-small tracking-10 text-darkGrey">{frame.socialPost.handle}</p>
                      </div>
                      <div className="mb-1">
                        <p className="text-small leading-small tracking-10">{frame.socialPost.desc}</p>
                      </div>
                      <div className="mb-2 flex items-center">
                        {frame.socialPost.socialIcon}
                        <p className="text-xs leading-small tracking-10 text-darkGrey">{frame.socialPost.socialDate}</p>
                      </div>
                      <div className="z-10 relative flex items-center justify-center">
                        {frame.socialPost.isVideo && (
                          <div className="z-20 absolute w-9 h-5 flex items-center justify-center rounded-20 bg-bodyText bg-opacity-50">
                            <IconPlay className="w-2 h-2 text-white" />
                          </div>
                        )}
                        <Img
                          className="w-39.5 h-auto"
                          fluid={frame.socialPost.media}
                          alt={`${frame.socialPost.name} media`}
                        />
                      </div>
                    </div>
                  </a>
                </div>
                <div className="w-full laptop:w-10/12 flex flex-col">
                  <div className="flex flex-col tablet:flex-row laptop:flex-row items-start">
                    <div className="-mt-8 tablet:mt-0 laptop:mt-0 mr-2.5 mb-2 tablet:mb-0 laptop:mb-0 h-9 w-9 rounded-full">
                      <Img className="h-full w-full rounded-full" fluid={frame.logo} alt={`${frame.heading} logo`} />
                    </div>
                    <div className="flex flex-col">
                      <div className="mb-1">
                        <h5 className="text-h6 leading-h6 text-darkGrey tracking-12 font-black">{frame.subheading}</h5>
                      </div>
                      <div className="mb-1.5">
                        <h4 className="text-h4 leading-h4 tracking-30 font-serif" style={{ color: frame.brandColor }}>
                          {frame.heading}
                        </h4>
                      </div>
                    </div>
                  </div>
                  {frame.desc && (
                    <div className="mb-2">
                      <p className="text-16 text-body leading-p tracking-10">{frame.desc}</p>
                    </div>
                  )}
                  {frame.isStatStyle ? (
                    <div
                      className={cx("w-full flex", {
                        "flex-col": frame.isStatStack,
                        "flex-wrap": !frame.isStatStack,
                      })}
                    >
                      <div
                        className={cx("mb-2", {
                          "w-full": frame.isStatStack,
                          "w-1/2 ": !frame.isStatStack,
                        })}
                      >
                        <h6 className="text-28 leading-h4 tracking-11 font-black" style={{ color: frame.brandColor }}>
                          {frame.statOne}
                        </h6>
                        <h5 className="text-16 text-body leading-p tracking-10 font-black">{frame.statOneType}</h5>
                      </div>
                      <div
                        className={cx("mb-2", {
                          "w-full": frame.isStatStack,
                          "w-1/2 ": !frame.isStatStack,
                        })}
                      >
                        <h6 className="text-28 leading-h4 tracking-11 font-black" style={{ color: frame.brandColor }}>
                          {frame.statTwo}
                        </h6>
                        <h5 className="text-16 text-body leading-p tracking-10 font-black">{frame.statTwoType}</h5>
                      </div>
                      <div
                        className={cx("mb-2", {
                          "w-full": frame.isStatStack || frame.isLastStatFull,
                          "w-1/2 ": !frame.isStatStack,
                        })}
                      >
                        <h6 className="text-28 leading-h4 tracking-11 font-black" style={{ color: frame.brandColor }}>
                          {frame.statThree}
                        </h6>
                        <h5 className="text-16 text-body leading-p tracking-10 font-black">{frame.statThreeType}</h5>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="mb-2.5">
                        <p className="text-16 text-body leading-p tracking-10">{frame.quote}</p>
                      </div>
                      <div className="flex">
                        <div className="mr-1.5 w-5.5 h-5.5 rounded-full">
                          <Img
                            className="w-5.5 h-5.5 rounded-full"
                            fluid={frame.authorAvatar}
                            alt={`${frame.authorName} avatar`}
                          />
                        </div>
                        <div className="flex flex-col">
                          <p className="text-16 text-body leading-p tracking-09">{frame.authorName}</p>
                          <p className="text-16 text-body leading-p tracking-09 font-black">{frame.authorRole}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
